import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import ApprovalCard from "./ApprovalCard";
import KycStatusBadge from "../../components/KycStatusBadge";

interface AccountHolderInfoCardProps {
  initials: string;
  // These display-only fields are rendered straight into JSX (as InfoItem
  // values / JSX children) and the callers feed them codegen-nullable values
  // (correlationName, getMetaValue results). Widen to ReactNode so the nullable
  // values pass through unchanged — the original JS rendered them the same way.
  firstName?: React.ReactNode;
  lastName?: React.ReactNode;
  email?: React.ReactNode;
  dob?: React.ReactNode;
  ssn?: string;
  userId?: React.ReactNode;
  apexAccount?: React.ReactNode;
  kycState?: React.ReactNode;
  phone?: React.ReactNode;
  silaWalletID?: React.ReactNode;
  // Inert extras passed by some callers (YellowPath/Address) that the original
  // JS never destructured/read — declared optional so JSX accepts them, body
  // ignores them exactly as before.
  submissionDate?: React.ReactNode;
  accountId?: React.ReactNode;
  kycStatus?: React.ReactNode;
  uploadRetries?: React.ReactNode;
}

interface InfoItemProps {
  label: string;
  value?: React.ReactNode;
  icon?: {
    component?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLSpanElement>;
  };
}

const InfoItem = ({ label, value, icon }: InfoItemProps) => (
  <div className="info-item mb-2">
    <p className="account-info-label">{label}:</p>
    <p className="account-info-value">
      {value}
      {icon && (
        <span
          style={{ cursor: "pointer", paddingLeft: 5 }}
          onClick={icon.onClick}
        >
          {icon.component}
        </span>
      )}
    </p>
  </div>
);

const AccountHolderInfoCard = ({
  initials,
  firstName,
  lastName,
  email,
  dob,
  ssn,
  userId,
  apexAccount,
  kycState,
  phone,
  silaWalletID,
}: AccountHolderInfoCardProps) => {
  const [showFullSsn, setShowFullSsn] = useState(false);

  const displayedSsn = showFullSsn ? ssn : ssn ? "***-**-" + ssn.slice(-4) : "";

  return (
    <ApprovalCard title="Account Holder Information">
      <div className="approval-account-holder">
        <div className="circle">
          <span className="icon">{initials}</span>
        </div>
        <div>
          <p className="name">
            {firstName} {lastName}
          </p>
          <KycStatusBadge
            // @ts-expect-error kycState is optional here but KycStatusBadge requires status: string; passing undefined preserves original JS behavior (badge renders "Unknown")
            status={kycState}
            className="custom-class"
            size="default"
          />
        </div>
      </div>

      <p className="account-title">Personal Information</p>
      <Row>
        <Col sm={12} md={6}>
          {firstName && <InfoItem label="First Name" value={firstName} />}
          <InfoItem label="DOB" value={dob} />
          {email && <InfoItem label="Email" value={email} />}
        </Col>

        <Col sm={12} md={6}>
          {lastName && <InfoItem label="Last Name" value={lastName} />}
          <InfoItem
            label="SSN"
            value={displayedSsn}
            icon={{
              component: showFullSsn ? <FiEyeOff /> : <FiEye />,
              onClick: () => setShowFullSsn(!showFullSsn),
            }}
          />
          {phone && <InfoItem label="Phone" value={phone} />}
        </Col>
      </Row>

      <p
        className="account-title"
        style={{ borderTop: "1px solid #F6F6F7", paddingTop: 25 }}
      >
        Account Information
      </p>
      <Row>
        <Col sm={12} md={6}>
          <InfoItem label="User ID" value={userId} />
          <InfoItem label="Sila Wallet ID" value={silaWalletID} />
        </Col>
        <Col sm={12} md={6}>
          {apexAccount && <InfoItem label="Apex Account" value={apexAccount} />}
          {kycState && <InfoItem label="KYC State" value={kycState} />}
        </Col>
      </Row>
    </ApprovalCard>
  );
};

export default AccountHolderInfoCard;
